import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { AIConciergeStats, TrendDirection } from '../adminDashboardTypes'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import NorthIcon from '@mui/icons-material/North'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'

const Container = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`

const Title = styled(Text)`
  font-weight: 600;
`

const ViewInsightsLink = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;
`

const StatBox = styled.div`
  background-color: ${(props) => props.theme.palette.grey.grey50};
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
`

const StatValue = styled(Text)`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const StatLabel = styled(Text)`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
`

const TrendBadge = styled.div<{ $direction: TrendDirection }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ $direction }) => {
    if ($direction === 'up') return palette.success.green600
    if ($direction === 'down') return palette.error.red600
    return palette.grey.grey600
  }};
`

const TopicContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: ${(props) => props.theme.palette.grey.grey50};
  border-radius: 0.5rem;
  margin-top: auto;
`

const TopicIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  color: ${palette.warning.yellow500};
`

const TopicText = styled(Text)`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

const TopicHighlight = styled.span`
  font-weight: 600;
  color: ${palette.text.primary};
`

interface AIConciergeWidgetProps {
  stats: AIConciergeStats
}

const AIConciergeWidget = ({ stats }: AIConciergeWidgetProps) => {
  const getArrowRotation = (direction: TrendDirection) => {
    if (direction === 'up') return 'rotate(0deg)'
    if (direction === 'down') return 'rotate(180deg)'
    return 'rotate(90deg)'
  }

  return (
    <Container>
      <Header>
        <Title variant='body1'>AI Concierge</Title>
        <ViewInsightsLink>
          View Insights <ArrowForwardIcon style={{ fontSize: '0.875rem' }} />
        </ViewInsightsLink>
      </Header>

      <StatsGrid>
        <StatBox>
          <StatValue>{stats.questionsHandled.toLocaleString()}</StatValue>
          <StatLabel>Questions Handled</StatLabel>
          <TrendBadge $direction={stats.questionsHandledTrend.direction}>
            <NorthIcon
              style={{
                fontSize: '0.625rem',
                transform: getArrowRotation(
                  stats.questionsHandledTrend.direction
                ),
              }}
            />
            {stats.questionsHandledTrend.label}
          </TrendBadge>
        </StatBox>

        <StatBox>
          <StatValue>{stats.autoResolved}%</StatValue>
          <StatLabel>Auto-Resolved</StatLabel>
          <TrendBadge $direction={stats.autoResolvedTrend.direction}>
            <NorthIcon
              style={{
                fontSize: '0.625rem',
                transform: getArrowRotation(stats.autoResolvedTrend.direction),
              }}
            />
            {stats.autoResolvedTrend.label}
          </TrendBadge>
        </StatBox>

        <StatBox>
          <StatValue>{stats.avgResponseTime}s</StatValue>
          <StatLabel>Avg Response</StatLabel>
          <TrendBadge $direction={stats.avgResponseTimeTrend.direction}>
            <NorthIcon
              style={{
                fontSize: '0.625rem',
                transform: getArrowRotation(
                  stats.avgResponseTimeTrend.direction
                ),
              }}
            />
            {stats.avgResponseTimeTrend.label}
          </TrendBadge>
        </StatBox>
      </StatsGrid>

      <TopicContainer>
        <TopicIcon>
          <TipsAndUpdatesIcon style={{ fontSize: '1.25rem' }} />
        </TopicIcon>
        <TopicText>
          <TopicHighlight>Top topic this week:</TopicHighlight> {stats.topTopic}{' '}
          ({stats.topTopicCount.toLocaleString()} questions)
        </TopicText>
      </TopicContainer>
    </Container>
  )
}

export default React.memo(AIConciergeWidget)
